import type {
  AddListItemRequest,
  ListItem,
  ListSummary,
  ReplaceListItemRequest,
} from '@lista/shared-validation';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { moveToIndex, type Neighbours, placeBetween } from '../ordering';
import { queryKeys } from '../query-keys';

export function useListItems(listId: string) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.lists.items(listId), locale],
    queryFn: () => services.listItems.list(listId),
  });
}

const itemsKey = (listId: string) => queryKeys.lists.items(listId);

/** Item changes bump the list version (ADR-0009): refresh the summary so later If-Match is current. */
function afterItemChange(qc: QueryClient, listId: string) {
  void qc.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
  void qc.invalidateQueries({ queryKey: queryKeys.recent });
  void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards, refetchType: 'none' });
}

function patchItems(qc: QueryClient, listId: string, update: (items: ListItem[]) => ListItem[]) {
  qc.setQueriesData<ListItem[]>({ queryKey: itemsKey(listId) }, (items) =>
    items ? update(items) : items,
  );
}

function patchCounts(
  qc: QueryClient,
  listId: string,
  delta: { items?: number; purchased?: number },
) {
  qc.setQueryData<ListSummary>(queryKeys.lists.detail(listId), (l) =>
    l
      ? {
          ...l,
          itemCount: l.itemCount + (delta.items ?? 0),
          purchasedCount: l.purchasedCount + (delta.purchased ?? 0),
        }
      : l,
  );
}

export type AddItemInput = Omit<Partial<AddListItemRequest>, 'id'>;

/**
 * Add (catalog item or custom name). Not optimistic - the server decides whether it merges
 * into an existing line - but the response lands in the cache without a refetch.
 */
export function useAddItem(listId: string) {
  const { services, newId } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddItemInput) => services.listItems.add(listId, { ...input, id: newId() }),
    onSuccess: (item) => cacheAdded(qc, listId, item),
  });
}

/** Puts a line the server returned into the cached items (a merge replaces the old line). */
function cacheAdded(qc: QueryClient, listId: string, item: ListItem) {
  let isNew = true;
  patchItems(qc, listId, (items) => {
    if (items.some((i) => i.id === item.id)) {
      isNew = false;
      return items.map((i) => (i.id === item.id ? item : i));
    }
    return insertInServerOrder(items, item);
  });
  if (isNew) patchCounts(qc, listId, { items: 1 });
  afterItemChange(qc, listId);
}

/** Server order: open lines first, then position byte-wise (the list query's ORDER BY). */
function serverOrder(a: ListItem, b: ListItem): number {
  if (a.purchased !== b.purchased) return a.purchased ? 1 : -1;
  return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
}

/**
 * A new line lands where the server put it (the end of its category group, FR-ITM-03), not
 * at the bottom: appending made the line jump into its group on the next refetch.
 */
function insertInServerOrder(items: readonly ListItem[], item: ListItem): ListItem[] {
  const at = items.findIndex((i) => serverOrder(item, i) < 0);
  return at < 0 ? [...items, item] : [...items.slice(0, at), item, ...items.slice(at)];
}

export interface AddToListsVars {
  listIds: string[];
  input: AddItemInput;
}

export interface AddToListsResult {
  added: string[];
  failed: Array<{ listId: string; error: unknown }>;
}

/**
 * Adds one item to several lists at once (one request per list, each with its own client id).
 * Lists that fail don't undo the others: the result says which ones went through.
 */
export function useAddItemToLists() {
  const { services, newId } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ listIds, input }: AddToListsVars): Promise<AddToListsResult> => {
      const settled = await Promise.allSettled(
        listIds.map((listId) => services.listItems.add(listId, { ...input, id: newId() })),
      );
      const result: AddToListsResult = { added: [], failed: [] };
      settled.forEach((s, i) => {
        const listId = listIds[i]!;
        if (s.status === 'fulfilled') {
          cacheAdded(qc, listId, s.value);
          result.added.push(listId);
        } else result.failed.push({ listId, error: s.reason });
      });
      return result;
    },
  });
}

export interface UpdateItemVars {
  item: ListItem;
  changes: Partial<ReplaceListItemRequest>;
}

/** Optimistic replace (quantity, unit, note, purchased, move). Rolls back on failure. */
export function useUpdateItem(listId: string) {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ item, changes }: UpdateItemVars) =>
      services.listItems.replace(listId, item.id, {
        quantity: item.quantity,
        unit: item.unit,
        note: item.note,
        purchased: item.purchased,
        ...changes,
      }),
    onMutate: async ({ item, changes }) => {
      await qc.cancelQueries({ queryKey: itemsKey(listId) });
      const snapshot = qc.getQueriesData<ListItem[]>({ queryKey: itemsKey(listId) });
      const summary = qc.getQueryData<ListSummary>(queryKeys.lists.detail(listId));
      patchItems(qc, listId, (items) =>
        items.map((i) =>
          i.id === item.id
            ? {
                ...i,
                ...changes,
                note: changes.note === undefined ? i.note : changes.note,
                purchasedAt:
                  changes.purchased === undefined
                    ? i.purchasedAt
                    : changes.purchased
                      ? new Date().toISOString()
                      : null,
              }
            : i,
        ),
      );
      if (changes.purchased !== undefined && changes.purchased !== item.purchased) {
        patchCounts(qc, listId, { purchased: changes.purchased ? 1 : -1 });
      }
      return { snapshot, summary };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.summary) qc.setQueryData(queryKeys.lists.detail(listId), ctx.summary);
    },
    onSuccess: (item) =>
      patchItems(qc, listId, (items) => items.map((i) => (i.id === item.id ? item : i))),
    onSettled: () => afterItemChange(qc, listId),
  });
}

/** Optimistic removal; the line comes back if the server refuses. */
export function useRemoveItem(listId: string) {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: ListItem) => services.listItems.remove(listId, item.id),
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: itemsKey(listId) });
      const snapshot = qc.getQueriesData<ListItem[]>({ queryKey: itemsKey(listId) });
      const summary = qc.getQueryData<ListSummary>(queryKeys.lists.detail(listId));
      patchItems(qc, listId, (items) => items.filter((i) => i.id !== item.id));
      patchCounts(qc, listId, { items: -1, purchased: item.purchased ? -1 : 0 });
      return { snapshot, summary };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.summary) qc.setQueryData(queryKeys.lists.detail(listId), ctx.summary);
    },
    onSettled: () => afterItemChange(qc, listId),
  });
}

/** Lines grouped for display: open lines by category order, then the bought ones. */
export function groupListItems(items: ListItem[], categoryOrder: (categoryId: string) => number) {
  const open = items.filter((i) => !i.purchased);
  const byCategory = new Map<string, ListItem[]>();
  for (const item of open)
    byCategory.set(item.categoryId, [...(byCategory.get(item.categoryId) ?? []), item]);
  const groups = [...byCategory.entries()]
    .sort(([a], [b]) => categoryOrder(a) - categoryOrder(b))
    .map(([categoryId, lines]) => ({ categoryId, lines }));
  return { groups, bought: items.filter((i) => i.purchased) };
}

export interface MoveItemVars {
  item: ListItem;
  neighbours: Neighbours;
}

/**
 * Drag-and-drop inside a list (custom order, ADR-0008). `move(visible, id, to)` takes the
 * open lines exactly as shown (after any search/category filter), reorders the cache
 * immediately and sends the line's neighbours. Rolls back if the server refuses.
 */
export function useMoveItem(listId: string) {
  const { services } = useCore();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ item, neighbours }: MoveItemVars) =>
      services.listItems.replace(listId, item.id, {
        quantity: item.quantity,
        unit: item.unit,
        note: item.note,
        purchased: item.purchased,
        ...neighbours,
      }),
    onMutate: async ({ item, neighbours }) => {
      await qc.cancelQueries({ queryKey: itemsKey(listId) });
      const snapshot = qc.getQueriesData<ListItem[]>({ queryKey: itemsKey(listId) });
      patchItems(qc, listId, (items) => placeBetween(items, item.id, neighbours));
      return { snapshot };
    },
    onError: (_e, _v, ctx) => ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data)),
    onSuccess: (saved) =>
      patchItems(qc, listId, (items) => items.map((i) => (i.id === saved.id ? saved : i))),
    onSettled: () => afterItemChange(qc, listId),
  });

  return {
    ...mutation,
    /** Moves line `id` to index `to` among the `visible` open lines. */
    move(
      visible: readonly ListItem[],
      id: string,
      to: number,
      options?: Parameters<typeof mutation.mutate>[1],
    ) {
      const result = moveToIndex(visible, id, to);
      const item = visible.find((i) => i.id === id);
      if (result && item) mutation.mutate({ item, neighbours: result.neighbours }, options);
    },
  };
}

/** Adds several lines (catalog items or brand/custom names) to a list in one request. */
export function useAddManyToList() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      items,
    }: {
      listId: string;
      items: Array<Partial<AddListItemRequest>>;
    }) => services.listItems.addMany(listId, items),
    onSuccess: (_items, { listId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.lists.items(listId) });
      void qc.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards });
      void qc.invalidateQueries({ queryKey: queryKeys.recent });
    },
  });
}
